/**
 * DbViewer API Service
 *
 * Connects DbViewer to PostgreSQL via the Node.js API.
 * Falls back to localStorage (seed data) if the API is unavailable.
 */

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function getAuthToken(): string | null {
  return localStorage.getItem('akt_auth_token');
}

function authHeaders(): Record<string, string> {
  const token = getAuthToken();
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

/** Build an Error that carries the HTTP status (so callers can detect 401, etc.). */
function httpError(res: Response, body: { message?: string }, fallback: string): Error {
  const e = new Error(body.message || fallback);
  (e as any).status = res.status;
  return e;
}

export async function fetchTableData(tableName: string): Promise<Record<string, any>[]> {
  const res = await fetch(`${API_BASE}/db/table/${tableName}`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw httpError(res, err, `Failed to fetch ${tableName}`);
  }
  return res.json();
}

/** Fetch ONE row by primary-key id (avoids pulling the whole table — used for heavy
 *  columns like dms_documents.file_base64). Returns null on 404. */
export async function fetchTableRow(
  tableName: string,
  id: number | string
): Promise<Record<string, any> | null> {
  const res = await fetch(`${API_BASE}/db/table/${tableName}/${id}`, {
    headers: authHeaders(),
  });
  if (res.status === 404) return null;
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw httpError(res, err, `Failed to fetch ${tableName} #${id}`);
  }
  return res.json();
}

export async function updateRow(
  tableName: string,
  id: number | string,
  updates: Record<string, any>
): Promise<Record<string, any>> {
  const res = await fetch(`${API_BASE}/db/table/${tableName}/${id}`, {
    method: 'PUT',
    headers: authHeaders(),
    body: JSON.stringify(updates),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw httpError(res, err, 'Update failed');
  }
  return res.json();
}

export async function insertRow(
  tableName: string,
  row: Record<string, any>
): Promise<Record<string, any>> {
  const res = await fetch(`${API_BASE}/db/table/${tableName}`, {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(row),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw httpError(res, err, 'Insert failed');
  }
  return res.json();
}

export async function deleteRow(
  tableName: string,
  id: number | string
): Promise<void> {
  const res = await fetch(`${API_BASE}/db/table/${tableName}/${id}`, {
    method: 'DELETE',
    headers: authHeaders(),
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({ message: res.statusText }));
    throw httpError(res, err, 'Delete failed');
  }
}

export async function isApiAvailable(): Promise<boolean> {
  try {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 3000);
    const res = await fetch(`${API_BASE}/health`, { signal: controller.signal });
    clearTimeout(timer);
    return res.ok;
  } catch {
    return false;
  }
}
